import { RefObject, useMemo, useReducer, useRef } from 'react';
import { FormikProps } from 'formik';
import { getFormikChangedProperties } from '../../shared/utils/formik';
import { EntityFormReducerActionsFactory } from '../reducer/factory';
import { useQuery } from '../../request/hooks/useQuery';
import { GenericApiResponse, PrimitiveTypes } from '../../shared/interfaces/generics';
import { AsyncTaskStatus, AsyncTaskStatusResponse } from '../../shared/interfaces/async-task';
import { EntityFormTrackers } from '../trackers/entity-form-trackers';
import { getErrorMessageFromAxiosError } from '../../request/utils';
import { useAnalyticsTracker } from '../../tracking/hooks/useAnalyticsTracker';
import type { EntityFormProps } from '../EntityForm';
import { useScrollToBottom } from '../../shared/hooks/useScrollToBottom';
import { removeObjectEntriesWithNull } from '../../shared/components/Calendar/utils';
import { useAsyncValidationForm } from '../../shared/components/AsyncValidationForm/hooks/useAsyncValidationForm';
import {
  TemplateForm,
  TemplateFormMode,
  TemplateFormState
} from '../../template-form/interfaces/template-form';
import { useDontMountAtFirst } from '../../shared/hooks/useDontMountAtFirst';
import { EntityFormReducerType } from '../reducer/interfaces';
import { ENTITY_FORM_REDUCER_INITIAL_STATE, EntityFormReducer } from '../reducer/entityFormReducer';
import { useEntityFormValidations } from './useEntityFormValidations';
import { getTemplateFormFields } from '../../template-form/utils/templateFormUtil';
import { EntityFormMetaData } from '../interfaces/entity-form';

export function useEntityForm({
  testConnectionResolver,
  testConnectionTaskIdResolver,
  formSubmitResolver,
  entity,
  formContainerRef,
  formikRef,
  form,
  onComplete,
  mode
}: {
  testConnectionResolver: EntityFormProps['testConnectionResolver'];
  testConnectionTaskIdResolver: EntityFormProps['testConnectionTaskIdResolver'];
  formSubmitResolver: EntityFormProps['formSubmitResolver'];
  entity: EntityFormProps['entity'];
  onComplete: EntityFormProps['onComplete'];
  formContainerRef: RefObject<HTMLElement>;
  formikRef: RefObject<FormikProps<TemplateFormState> | null>;
  form: TemplateForm<EntityFormMetaData>;
  mode: TemplateFormMode;
}) {
  const [state, dispatch] = useReducer<EntityFormReducerType>(
    EntityFormReducer,
    ENTITY_FORM_REDUCER_INITIAL_STATE
  );
  const { current: reducerActions } = useRef(EntityFormReducerActionsFactory(dispatch));

  const { eventTrack } = useAnalyticsTracker();
  const { scrollToBottom } = useScrollToBottom(formContainerRef);
  const { runValidationsAndSubmit, isValidating } = useAsyncValidationForm();

  const formFields = useRef(getTemplateFormFields(form));

  const validationSchema = useEntityFormValidations(
    entity,
    form.validationSchema,
    form.initialValues,
    (fieldName: string) => {
      return formFields.current[fieldName]?.renderExpression
        ? !!formFields.current[fieldName].renderExpression!.evaluate(
            (formikRef.current?.values ?? form.initialValues) as unknown as Record<
              string,
              PrimitiveTypes
            >,
            key => key
          )
        : true;
    }
  );

  const { fetch: pollTestConnection } = useQuery({
    dataSource$: () => testConnectionResolver(state.testConnectionTaskId!),
    pollInterval: 10000,
    terminatePollOnError: true,
    pollUntil: res => {
      const { task_status: taskStatus } = (res as GenericApiResponse<AsyncTaskStatusResponse>).data;
      return taskStatus === AsyncTaskStatus.EXECUTING;
    },
    fetchSuccess: res => {
      const { task_status: taskStatus, error_message: errorMessage } = (
        res as GenericApiResponse<AsyncTaskStatusResponse>
      ).data;

      const isTestConnectionSuccessful = taskStatus === AsyncTaskStatus.EXECUTED;
      const isTestConnectionFailed = taskStatus === AsyncTaskStatus.FAILED;

      if (isTestConnectionSuccessful) {
        eventTrack({
          action: EntityFormTrackers.TRACKER_ENTITY_FORM_TEST_CONNECTION_SUCCESS,
          properties: {
            entity
          }
        });

        reducerActions.saveTestConnectionSuccess();
        scrollToBottom();
      }

      if (isTestConnectionFailed) {
        eventTrack({
          action: EntityFormTrackers.TRACKER_ENTITY_FORM_TEST_CONNECTION_FAILURE,
          properties: {
            entity,
            failureReason: errorMessage
          }
        });

        reducerActions.saveTestConnectionError(errorMessage!);
        scrollToBottom();
      }
    },
    fetchError: e => {
      const errorMessage = getErrorMessageFromAxiosError(e);

      eventTrack({
        action: EntityFormTrackers.TRACKER_ENTITY_FORM_TEST_CONNECTION_FAILURE,
        properties: {
          entity,
          failureReason: errorMessage
        }
      });

      reducerActions.saveTestConnectionError(errorMessage);
      scrollToBottom();
    }
  });

  useDontMountAtFirst(() => {
    if (state.testConnectionSuccessful) {
      saveForm();
    }
  }, [state.testConnectionSuccessful]);

  useDontMountAtFirst(() => {
    if (state.testConnectionTaskId) {
      pollTestConnection(state.testConnectionTaskId);
    }
  }, [state.testConnectionTaskId]);

  const getFormSubmissionData = () => {
    let finalValues;

    if (formikRef.current?.values) {
      finalValues = removeObjectEntriesWithNull(formikRef.current.values);

      if (mode !== 'CREATE') {
        finalValues = getFormikChangedProperties(finalValues, formikRef.current.initialValues);
      }
    } else {
      finalValues = {};
    }

    return finalValues as TemplateFormState;
  };

  const saveForm = () => {
    reducerActions.saveForm();

    eventTrack({
      action: EntityFormTrackers.TRACKER_ENTITY_FORM_FORM_SAVE,
      properties: {
        entity
      }
    });

    formSubmitResolver(getFormSubmissionData())
      .then(res => {
        eventTrack({
          action: EntityFormTrackers.TRACKER_ENTITY_FORM_FORM_SAVE_SUCCESS,
          properties: {
            entity
          }
        });

        onComplete(res.data);
      })
      .catch(e => {
        eventTrack({
          action: EntityFormTrackers.TRACKER_ENTITY_FORM_FORM_SAVE_FAILURE,
          properties: {
            entity,
            error: e
          }
        });

        reducerActions.saveFormError(getErrorMessageFromAxiosError(e));
      });
  };

  const onFormValidationError = (errors: unknown) => {
    eventTrack({
      action: EntityFormTrackers.TRACKER_ENTITY_FORM_FORM_VALIDATION_FAILURE,
      properties: {
        entity,
        errors
      }
    });
  };

  const fetchTestConnectionTaskId = () => {
    reducerActions.initTestConnection();

    testConnectionTaskIdResolver(getFormSubmissionData())
      .then(res => {
        reducerActions.saveTestConnectionTaskId(res.data.task_request_id);
      })
      .catch(e => {
        const errorMessage = getErrorMessageFromAxiosError(e);
        reducerActions.saveTestConnectionError(errorMessage);
      });
  };

  const submitForm = () => {
    eventTrack({
      action: EntityFormTrackers.TRACKER_ENTITY_FORM_FORM_TEST_AND_SAVE_CLICK,
      properties: {
        entity
      }
    });

    reducerActions.submitForm();

    setTimeout(() => {
      scrollToBottom();
    }, 0);

    runValidationsAndSubmit(
      formikRef.current! as FormikProps<unknown>,
      fetchTestConnectionTaskId,
      onFormValidationError
    );
  };

  const inProgressBannerContent = useMemo(() => {
    if (state.submitForm && isValidating) {
      return 'Validating inputs...';
    }

    if (state.testConnectionRunning) {
      return 'Trying to establish a connection. Can take up to 2 minutes';
    }

    if (state.savingForm) {
      return 'Connection Successful. Saving form...';
    }

    return undefined;
  }, [state.testConnectionRunning, isValidating, state.savingForm, state.submitForm]);

  const isSubmitDisabled = state.savingForm || state.testConnectionRunning || isValidating;
  const showErrorBanner = !!(state.saveFormError || state.testConnectionError);
  const errorBannerCopy = state.saveFormError
    ? `Failed to save the form. ${state.saveFormError}`
    : state.testConnectionError;
  const showInProgressBanner =
    state.savingForm || state.testConnectionRunning || (state.submitForm && isValidating);

  return {
    submitForm,
    inProgressBannerContent,
    isSubmitDisabled,
    showErrorBanner,
    errorBannerCopy,
    showInProgressBanner,
    isValidating,
    state,
    validationSchema,
    showElapsedTime: state.testConnectionRunning
  };
}
